import { useRef, useState } from 'react';
import { formatRelativeTime, getCommentLabel, getPostSplit, getPostTypeOption, getTopicLabel, getTopicStyle, isOpenThought, userOwnsPost, userOwnsThreadItem } from '../utils/hixrs';
import { getTotalComments } from '../utils/threads';
import { readCompressedImage } from '../services/imageUpload';
import { SplitBar } from './common';

export function PostModal({ post, onClose, userVote, onVote, onComment, onReply, onCommentLike, onCommentDelete, onPostDelete, onPostEdit, onFlip, hasFlipped, onConfirmRequest, requireLogin, user, profile, onReport, onProfileOpen }) {
  const [newComment, setNewComment] = useState('');
  const [imgPreview, setImgPreview] = useState(null);
  const fileRef = useRef();
  const voted = userVote;
  const oppositeSide = voted === 'a' ? 'b' : 'a';
  const oppositeLabel = oppositeSide === 'a' ? post.sideA : post.sideB;
  const [commentTab, setCommentTab] = useState('all');
  const [commentError, setCommentError] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});
  const { pctA, pctB } = getPostSplit(post);
  const userPct = voted === 'a' ? pctA : pctB;
  const isMajority = voted === 'a' ? pctA >= pctB : pctB >= pctA;
  const comments = post.comments || [];
  const postTypeOption = getPostTypeOption(post.postType);
  const thought = isOpenThought(post);
  const hasCommentAccess = thought || voted;
  const isPostOwner = userOwnsPost(post, user, profile);
  const commentsWithIndex = comments.map((comment, index) => ({ ...comment, __index: index }));
  const visibleComments = commentsWithIndex.filter(c => {
    if (thought) return true;
    if (commentTab === 'mine') return c.side === voted;
    if (commentTab === 'other') return voted && c.side !== voted;
    return true;
  });
  const commentTotal = getTotalComments(comments);
  const postTime = formatRelativeTime(post.createdAt);
  const topReason = visibleComments
    .filter(c => !c.deleted && (c.likes || 0) > 0)
    .sort((a, b) => (b.likes || 0) - (a.likes || 0))[0];
  const topReasonKey = topReason ? String(topReason.__index) : '';
  const pickedLabel = thought ? 'Open Thought' : voted === 'a' ? post.sideA : post.sideB;

  const flattenReplies = (replies = [], parentPath = []) => replies.flatMap((reply, index) => {
    const replyPath = [...parentPath, index];
    return [{ comment: reply, path: replyPath }, ...flattenReplies(reply.replies || [], replyPath)];
  });

  const toggleReplies = (key) => {
    setExpandedReplies(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleComment = () => {
    requireLogin(async () => {
      if (sendingComment) return;
      if (!newComment.trim() && !imgPreview) {
        setCommentError(replyTarget ? 'Write a reply before sending.' : thought ? 'Write a response before sending.' : 'Write your side before sending.');
        return;
      }
      if (!thought && !voted) return;

      setSendingComment(true);
      setCommentError('');
      const commentSide = thought ? 'thought' : voted;
      try {
        if (replyTarget) {
          await onReply(post.id, replyTarget.path, newComment, commentSide, imgPreview);
          setExpandedReplies(prev => ({ ...prev, [String(replyTarget.path?.[0] ?? '')]: true }));
        } else {
          await onComment(post.id, newComment, commentSide, imgPreview);
        }
        setNewComment('');
        setImgPreview(null);
        setReplyTarget(null);
        if (fileRef.current) fileRef.current.value = '';
      } catch (e) {
        console.error(e);
        setCommentError('Could not send. Try again.');
      } finally {
        setSendingComment(false);
      }
    });
  };

  const handleImg = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setCommentError('Choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setCommentError('Choose an image under 8 MB.');
      e.target.value = '';
      return;
    }
    try {
      const compressed = await readCompressedImage(file, 1000, 0.76);
      if (compressed.length > 1600000) {
        setCommentError('This image is still too large after compression. Try a smaller one.');
        e.target.value = '';
        return;
      }
      setImgPreview(compressed);
      setCommentError('');
    } catch (err) {
      console.error(err);
      setCommentError('Could not read that image. Try another file.');
      e.target.value = '';
    }
  };

  const handleDeleteComment = (path) => {
    requireLogin(() => {
      onConfirmRequest({
        title: 'Delete comment?',
        message: "Are you sure you want to delete your comment? You can't undo this.",
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        variant: 'danger',
        onConfirm: async () => {
          await onCommentDelete(post.id, path);
          if (replyTarget && replyTarget.path.join('-') === path.join('-')) {
            setReplyTarget(null);
          }
        }
      });
    });
  };

  const handleFlipSide = () => {
    requireLogin(() => {
      if (!voted || hasFlipped) return;
      onConfirmRequest({
        title: 'Flip side?',
        message: 'Are you sure you want to flip? You can only flip once.',
        confirmLabel: 'Flip',
        cancelLabel: 'Cancel',
        variant: 'success',
        onConfirm: () => onFlip(post.id, oppositeSide)
      });
    });
  };

  const renderComment = (comment, path, options = {}) => {
    const { isReply = false, isTopReason = false } = options;
    const liked = Boolean(user && comment.likedBy?.includes(user.uid));
    const isOwner = userOwnsThreadItem(comment, user, profile);
    const isDeleted = Boolean(comment.deleted);
    const avatarInitial = isDeleted ? 'D' : comment.user?.[0]?.toUpperCase() || 'U';
    const commentProfile = { uid: comment.userId, displayName: comment.user, photoURL: comment.userPhoto };
    const pathKey = path.join('-');
    const rootKey = String(path[0] ?? '');
    const replyItems = isReply ? [] : flattenReplies(comment.replies || [], path);
    const repliesOpen = Boolean(expandedReplies[rootKey]);
    const sideClass = thought ? 'side-thought' : comment.side === 'a' ? 'side-a' : 'side-b';
    const sideTagClass = comment.side === 'a' ? 'red-tag' : 'blue-tag';
    const sideLabel = comment.side === 'a' ? post.sideA : post.sideB;
    return (
      <div key={comment.id || comment.createdAt || pathKey} className={`comment-card ${sideClass} ${isReply ? 'is-reply' : ''} ${isDeleted ? 'deleted-comment' : ''}`}>
        <div className="comment-card-row">
          <button type="button" className="cmt-av profile-trigger" disabled={isDeleted} onClick={() => !isDeleted && onProfileOpen(commentProfile)}>{!isDeleted && comment.userPhoto ? <img src={comment.userPhoto} alt="commenter" /> : avatarInitial}</button>
          <div className="comment-card-main">
            <div className="comment-card-head">
              <button type="button" className="comment-user profile-name-btn" disabled={isDeleted} onClick={() => !isDeleted && onProfileOpen(commentProfile)}>{isDeleted ? 'Deleted comment' : comment.user}</button>
              {!isDeleted && <span className="comment-time">{formatRelativeTime(comment.createdAt)}</span>}
              {!isDeleted && isTopReason && <span className="top-reason-pill">Top reason</span>}
              {!isDeleted && !thought && <span className={`comment-side ${sideTagClass}`}>{sideLabel}</span>}
            </div>
            {isReply && !isDeleted && <div className="reply-context">Reply in this Hix</div>}
            {isDeleted ? (
              <div className="comment-deleted-text">This comment was deleted.</div>
            ) : (
              <>
                {comment.text && <div className="comment-text">{comment.text}</div>}
                {comment.image && <img src={comment.image} alt="comment" className="comment-img" />}
                <div className="comment-card-actions">
                  <button className={`comment-heart ${liked ? 'liked' : ''}`} onClick={() => requireLogin(() => onCommentLike(post.id, path))} aria-label={liked ? 'Unlike comment' : 'Like comment'} title={liked ? 'Unlike comment' : 'Like comment'}>
                    <span className="comment-heart-icon">&#10084;</span><span>{comment.likes || 0}</span>
                  </button>
                  <button className="thread-action" onClick={() => requireLogin(() => setReplyTarget({ path, user: comment.user }))}>Reply</button>
                  <button className="thread-action" onClick={() => requireLogin(() => onReport({ type: 'comment', postId: post.id, commentPath: path, preview: comment.text || 'Image comment' }))}>Report</button>
                  {isOwner && <button className="thread-action danger" onClick={() => handleDeleteComment(path)}>Delete</button>}
                </div>
              </>
            )}
          </div>
        </div>
        {!isReply && replyItems.length > 0 && (
          <>
            <button type="button" className="view-replies-btn" onClick={() => toggleReplies(rootKey)}>
              {repliesOpen ? 'Hide replies' : `View ${replyItems.length} ${replyItems.length === 1 ? 'reply' : 'replies'}`}
            </button>
            {repliesOpen && (
              <div className="flat-replies">
                {replyItems.map(item => renderComment(item.comment, item.path, { isReply: true }))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && onClose()}>
      <div className={`modal thread-modal hix-theme-${postTypeOption.key} ${thought ? 'open-thought-modal' : ''}`}>
        <div className="modal-head">
          <button type="button" className="modal-back-btn" onClick={onClose}>Back</button>
          <div className="modal-head-left">
            <span className="post-topic modal-topic" style={getTopicStyle(post.topic)}>{getTopicLabel(post.topic)}</span>
            <span className="modal-meta">{thought ? `${postTypeOption.label} - ${commentTotal} ${getCommentLabel(commentTotal)} - ${postTime}` : `${postTypeOption.label} - ${post.totalVotes || 0} lock-ins - Split ${post.heat || 50}% - ${postTime}`}</span>
          </div>
          {!user && (
            <button
              type="button"
              className="modal-guest-hint"
              onClick={() => requireLogin(() => {})}>
              {voted ? 'Save identity' : 'Sign in to participate'}
            </button>
          )}
          {isPostOwner && <button className="modal-report-btn" onClick={() => requireLogin(() => onPostEdit(post))}>Edit</button>}
          {isPostOwner && <button className="modal-report-btn danger" onClick={() => requireLogin(() => onPostDelete(post))}>Delete</button>}
          <button className="modal-report-btn" onClick={() => requireLogin(() => onReport({ type: 'post', postId: post.id, preview: post.question }))}>Report</button>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="modal-question">{post.question}</div>
          {post.image && <img className="modal-post-image" src={post.image} alt="Hix attachment" />}
          {hasCommentAccess ? (
            <>
              {!thought && <div className="voted-card">
                <div className="vc-sides"><span className="red-text">{post.sideA}</span><span className="blue-text">{post.sideB}</span></div>
                <SplitBar pctA={pctA} />
                <div className="result-nums"><span className="red-text">{pctA}%</span><span className="blue-text">{pctB}%</span></div>
                <div className={`verdict ${isMajority ? 'majority' : 'minority'}`} style={{ marginTop: 10 }}>{isMajority ? `You are in the majority (${userPct}%)` : `You are in the ${userPct}% minority. Defend it.`}</div>
                <div className="flip-action-row">
                  {hasFlipped ? (
                    <span className="flip-used-pill">Flip used</span>
                  ) : (
                    <button className="flip-action-btn" onClick={handleFlipSide}>Flip to {oppositeLabel}</button>
                  )}
                </div>
              </div>}
              {thought && <div className="open-thought-modal-note">Open discussion. No lock-in needed.</div>}
              <div className="modal-stats"><span className="stat-pill">{commentTotal} {getCommentLabel(commentTotal)}</span>{!thought && <span className="stat-pill">Split {post.heat || 50}%</span>}{!thought && <span className="stat-pill">{post.totalVotes || 0} lock-ins</span>}</div>
              {!thought && <div className="side-tabs">
                <button className={`side-tab ${commentTab === 'all' ? 'active-tab' : ''}`} onClick={() => setCommentTab('all')}>All</button>
                <button className={`side-tab ${commentTab === 'mine' ? 'active-tab' : ''}`} onClick={() => setCommentTab('mine')}>My side</button>
                <button className={`side-tab ${commentTab === 'other' ? 'active-tab' : ''}`} onClick={() => setCommentTab('other')}>Other side</button>
              </div>}
              <div className="comment-section-head">
                <div>
                  <div className="cmts-label">Comments</div>
                  <p>{thought ? (commentTotal > 0 ? 'People are responding to this thought.' : 'Be the first response.') : (commentTotal > 0 ? 'Reasons from people who locked in.' : 'Be the first reason in the room.')}</p>
                </div>
                {voted && !thought && <span className={`composer-side-pill ${voted === 'a' ? 'red-tag' : 'blue-tag'}`}>You picked {pickedLabel}</span>}
                {thought && <span className="composer-side-pill thought-pill">Open Thought</span>}
              </div>
              {visibleComments.length === 0 && <div className="comments-empty">{thought ? 'No comments yet. Add the first response.' : 'No comments on this side yet. Add the first reason.'}</div>}
              <div className="comment-list-modern">
                {visibleComments.map((comment) => renderComment(comment, [comment.__index], { isTopReason: String(comment.__index) === topReasonKey }))}
              </div>
            </>
          ) : (
            <div className="vote-to-unlock">
              <div className="vtu-title">Lock in a side first</div>
              <div className="vtu-text">The discussion unlocks after you lock in.</div>
              <div className="vote-row modal-vote-row">
                <button className="vote-btn red-btn" onClick={() => onVote(post.id, 'a')}><span>{post.sideA}</span></button>
                <button className="vote-btn blue-btn" onClick={() => onVote(post.id, 'b')}><span>{post.sideB}</span></button>
              </div>
            </div>
          )}
        </div>
        {hasCommentAccess && (
          <div className="modal-input">
            <div className="comment-composer-main">
              {replyTarget && <div className="replying-banner">Replying to {replyTarget.user}<button onClick={() => setReplyTarget(null)}>Cancel</button></div>}
              {commentError && <div className="comment-error">{commentError}</div>}
              {imgPreview && <div className="img-preview-wrap"><img src={imgPreview} alt="preview" className="img-preview" /><button className="img-remove" onClick={() => setImgPreview(null)}>&times;</button></div>}
              <div className="comment-input-row comment-composer-card">
                <div className="composer-topline">
                  <span className={`composer-side-pill ${thought ? 'thought-pill' : voted === 'a' ? 'red-tag' : 'blue-tag'}`}>{thought ? 'Open Thought' : `You picked ${pickedLabel}`}</span>
                  <span>{replyTarget ? 'Reply' : thought ? 'Your response' : 'Your reason'}</span>
                </div>
                <textarea className="mi-input" rows={2} placeholder={replyTarget ? `Reply to ${replyTarget.user}...` : thought ? 'Add your take on this thought...' : 'Why did you pick this side?'} maxLength={180} value={newComment} onChange={e => setNewComment(e.target.value)} />
                <div className="comment-actions-row"><button className="img-btn" onClick={() => fileRef.current.click()}>Attach</button><input type="file" ref={fileRef} accept="image/*" onChange={handleImg} style={{ display: 'none' }} /><div className="char-count">{newComment.length}/180</div></div>
              </div>
            </div>
            <button className="send-btn" onClick={handleComment} disabled={sendingComment}>{sendingComment ? 'Sending' : replyTarget ? 'Reply' : 'Send'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

